// LinkedTicketsPanel — V1 parent-child ticket relationships UI surface.
//
// Timestamps: API returns relation and child ticket times as ISO-8601 OffsetDateTime
// strings already shifted to `app.display-timezone` (same contract as TicketReplyResponse).
// This panel uses `useDisplayTimezone()` from AppConfigContext for `toLocaleString({ timeZone })`
// so rendered wall-clock matches TicketDetail reply rows — never browser-local time.
// Renders three states based on the bundle returned by GET /api/ticket/{id}/relations:
//
//   currentTicketRole === 'parent'      → header + Children table + "Attach existing"/"Create child" buttons
//   currentTicketRole === 'child'       → header + Parent breadcrumb (with read-only banner explaining nesting)
//   currentTicketRole === 'standalone'  → header + empty state + "Attach existing"/"Create child" buttons
//
// V1 invariants this component embodies (so the UI cannot ask the backend to do something
// it will refuse):
//   * "Attach existing" and "Create child" buttons appear ONLY when the current ticket is
//     parent or standalone — never when it is already a child (V8: child can't become a
//     parent in V1).
//   * Unlink confirmation always shows the *other* ticket's number so the agent can't
//     misclick and detach the wrong row.
//   * The whole panel is gated upstream by `useTicketRelationsEnabled()`; this file
//     assumes the feature flag has already been checked by the parent and renders nothing
//     when no `bundle` has loaded yet.
//
// The panel does NOT own the network layer. The parent (TicketDetail) passes:
//   - bundle:     the latest RelationsBundleResponse (or null while loading)
//   - isLoading:  bool
//   - onRefresh:  () => Promise<void>     — re-fetches the bundle after a write
//   - onOpenAttach: () => void            — opens the AttachExistingTicketModal
//   - onOpenCreateChild: () => void       — opens the CreateChildTicketModal
//   - onUnlink:   (relationId, otherTicketNumber) => Promise<void>

import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  GitBranch,
  Link2,
  Plus,
  Unlink,
  AlertTriangle,
  Trash2,
  Inbox,
} from 'lucide-react';
import { useDisplayTimezone } from '../../contexts/AppConfigContext';

const formatLinkedAt = (iso, tz) => {
  if (!iso) return '—';
  if (!tz) return '…';
  try {
    return new Date(iso).toLocaleString('en-GB', { timeZone: tz, hour12: false });
  } catch {
    return '—';
  }
};

const StatusPill = ({ statusName }) => {
  if (!statusName) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
      {statusName}
    </span>
  );
};

const SlaBadge = ({ slaBreach, escalated }) => {
  if (!slaBreach && !escalated) return null;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        escalated
          ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
          : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
      }`}
      title={escalated ? 'Escalated' : 'SLA breached'}
    >
      <AlertTriangle className="h-3 w-3 mr-1" />
      {escalated ? 'Escalated' : 'SLA breach'}
    </span>
  );
};

const PanelHeader = ({ role, childCount, canLink, onOpenAttach, onOpenCreateChild }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <GitBranch className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Linked Tickets
      </h3>
      {role === 'parent' && childCount > 0 && (
        <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
          {childCount} {childCount === 1 ? 'child' : 'children'}
        </span>
      )}
    </div>
    {canLink && (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onOpenAttach}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <Link2 className="h-4 w-4 mr-1.5" />
          Attach existing
        </button>
        {/* <button
          type="button"
          onClick={onOpenCreateChild}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Create child
        </button> */}
      </div>
    )}
  </div>
);

const ChildRow = ({ child, onUnlink, displayTimezone }) => {
  const handleUnlink = () => {
    const ok = window.confirm(
      `Unlink child ticket ${child.ticketNumber}?\n\n` +
        `The child ticket will continue to exist on its own — only the parent-child link is removed.`,
    );
    if (ok) onUnlink(child.relationId, child.ticketNumber);
  };

  return (
    <tr className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40">
      <td className="px-3 py-2.5 text-sm">
        <RouterLink
          to={`/agent/tickets/${child.ticketId}`}
          className="font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {child.ticketNumber}
        </RouterLink>
      </td>
      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate" title={child.subject}>
        {child.subject || '—'}
      </td>
      <td className="px-3 py-2.5 text-sm">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatusPill statusName={child.statusName || child.statusCode} />
          <SlaBadge slaBreach={child.slaBreach} escalated={child.escalated} />
        </div>
      </td>
      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300">
        {child.assigneeName || (child.assigneeId ? `User ${child.assigneeId}` : 'Unassigned')}
      </td>
      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {formatLinkedAt(child.linkedAt, displayTimezone)}
      </td>
      <td className="px-3 py-2.5 text-right">
        <button
          type="button"
          onClick={handleUnlink}
          className="inline-flex items-center p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
          title={`Unlink ${child.ticketNumber}`}
        >
          <Unlink className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
};

const ChildrenTable = ({ children, onUnlink, displayTimezone }) => (
  <div className="overflow-x-auto -mx-2">
    <table className="w-full">
      <thead>
        <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          <th className="px-3 py-2">Ticket</th>
          <th className="px-3 py-2">Subject</th>
          <th className="px-3 py-2">Status</th>
          <th className="px-3 py-2">Assignee</th>
          <th className="px-3 py-2">Linked</th>
          <th className="px-3 py-2 sr-only">Actions</th>
        </tr>
      </thead>
      <tbody>
        {children.map((c) => (
          <ChildRow
            key={c.relationId}
            child={c}
            onUnlink={onUnlink}
            displayTimezone={displayTimezone}
          />
        ))}
      </tbody>
    </table>
  </div>
);

const ParentBreadcrumb = ({ parent, onUnlink, displayTimezone }) => {
  const handleUnlink = () => {
    const ok = window.confirm(
      `Detach this ticket from parent ${parent.ticketNumber}?\n\n` +
        `Both tickets will continue to exist independently — only the parent-child link is removed.`,
    );
    if (ok) onUnlink(parent.relationId, parent.ticketNumber);
  };

  return (
    <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-blue-700 dark:text-blue-300 mb-1">
            Linked under parent
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {parent.isParentDeleted ? (
              <span className="font-medium text-gray-500 dark:text-gray-400 line-through">
                {parent.ticketNumber} (deleted)
              </span>
            ) : (
              <RouterLink
                to={`/agent/tickets/${parent.ticketId}`}
                className="font-medium text-blue-700 dark:text-blue-300 hover:underline"
              >
                {parent.ticketNumber}
              </RouterLink>
            )}
            <StatusPill statusName={parent.statusName || parent.statusCode} />
          </div>
          <div className="mt-1 text-sm text-gray-700 dark:text-gray-300 truncate" title={parent.subject}>
            {parent.subject || '—'}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Linked at {formatLinkedAt(parent.linkedAt, displayTimezone)}
          </div>
        </div>
        <button
          type="button"
          onClick={handleUnlink}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/30"
        >
          <Trash2 className="h-4 w-4 mr-1.5" />
          Unlink from parent
        </button>
      </div>
      <div className="mt-3 text-xs text-blue-700/80 dark:text-blue-300/80">
        Note: this ticket is itself a child, so it cannot have its own children
        (single-level relationships only).
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div className="rounded-md border border-dashed border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900/30 py-8 px-4 text-center">
    <Inbox className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
    <p className="text-sm text-gray-500 dark:text-gray-400">
      No linked tickets yet. Attach an existing ticket as a child or create a new one.
    </p>
  </div>
);

const LinkedTicketsPanel = ({
  bundle,
  isLoading,
  onOpenAttach,
  onOpenCreateChild,
  onUnlink,
}) => {
  const displayTimezone = useDisplayTimezone();

  // Memoize derived role flags so re-renders don't re-execute the conditions.
  const view = useMemo(() => {
    if (!bundle) return { role: null, canLink: false };
    const role = bundle.currentTicketRole;
    // Only standalone or parent tickets can have new links added in V1.
    const canLink = role === 'standalone' || role === 'parent';
    return { role, canLink };
  }, [bundle]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <PanelHeader
        role={view.role}
        childCount={bundle?.childCount || 0}
        canLink={view.canLink}
        onOpenAttach={onOpenAttach}
        onOpenCreateChild={onOpenCreateChild}
      />

      {isLoading && (
        <div className="text-sm text-gray-500 dark:text-gray-400 py-2">Loading linked tickets…</div>
      )}

      {!isLoading && bundle?.currentTicketRole === 'child' && bundle.parent && (
        <ParentBreadcrumb
          parent={bundle.parent}
          onUnlink={onUnlink}
          displayTimezone={displayTimezone}
        />
      )}

      {!isLoading && bundle?.currentTicketRole === 'parent' && bundle.children?.length > 0 && (
        <ChildrenTable
          children={bundle.children}
          onUnlink={onUnlink}
          displayTimezone={displayTimezone}
        />
      )}

      {!isLoading && bundle?.currentTicketRole === 'standalone' && <EmptyState />}
    </div>
  );
};

export default LinkedTicketsPanel;
